import React from "react";
import T from '../../components/common/T';
import { useNavigate } from "react-router-dom";
import { useTasks } from "../../context/TaskContext";
import { toast } from "react-toastify";
import DataTable from "../../components/common/DataTables.jsx";
import PageHeader from "../../components/common/PageHeader";

import { useProjects } from "../../context/ProjectContext";
import { useEmployees } from "../../context/EmployeeContext";
import { useAuth } from "../../context/AuthContext";

import TableActionButtons from "../../components/common/TableActionButtons";
import { formatDate } from "../../utils/dateUtils";

const TaskListView = ({ tasks }) => {
  const navigate = useNavigate();
  const { deleteTask } = useTasks();
  
  const { projects } = useProjects();
  const { employees } = useEmployees();
  const { checkAccess } = useAuth();

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this task?")) return;
    await deleteTask(id);
  };

  const getStatusColor = (status) => {
    if (status === "Completed") return "bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800/60";
    if (status === "In Progress") return "bg-yellow-50 dark:bg-amber-950/40 text-yellow-700 dark:text-amber-400 border-yellow-200 dark:border-amber-800/60";
    return "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/60";
  };

  const getProjectName = (task) => {
    const proj = projects.find(p => String(p.id) === String(task.projectId || task.project_id));
    const name = task.projectName || proj?.name;
    const location = task.projectLocation || proj?.location;

    if (!name) return <span className="text-gray-400 dark:text-slate-500 italic text-[11px]"><T>No Project</T></span>;

    return (
      <button 
        onClick={() => navigate(`/admin/project/${task.projectId || task.project_id}`)}
        className="text-left group transition flex flex-col"
        title="View Project Details"
      >
        <span className="text-blue-600 dark:text-blue-400 font-semibold group-hover:underline text-xs flex items-center gap-1">
          📁 {name}
        </span>
        {location && (
          <span className="text-[10px] text-gray-400 dark:text-slate-500 font-normal truncate max-w-[180px]">
            📍 {location}
          </span>
        )}
      </button>
    );
  };

  const getAssigneeBadge = (task) => {
    // 1. Direct from task data (populated by backend)
    let name = task.assignedName && !task.assignedName.startsWith("Employee #") ? task.assignedName : null;
    if (!name && task.assignedTo && !task.assignedTo.startsWith("Employee #")) name = task.assignedTo;

    // 2. Fallback to EmployeeContext lookup
    let role = task.assignedRole || "";
    if (!name && (task.assignedEmployeeId || task.employeeId || task.assigned_to)) {
      const eid = String(task.assignedEmployeeId || task.employeeId || task.assigned_to);
      const emp = employees.find(e => String(e.id) === eid);
      if (emp) {
        name = emp.employeeName || emp.name;
        if (!role) role = emp.role || emp.trade || "";
      }
    }

    if (name && name !== "Unassigned") {
      const initial = name.charAt(0).toUpperCase();
      return (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-bold text-xs flex items-center justify-center shrink-0 border border-blue-200 dark:border-blue-800">
            {initial}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-gray-900 dark:text-white text-xs truncate max-w-[150px]">
              {name}
            </span>
            {role && (
              <span className="text-[10px] text-gray-500 dark:text-slate-400 font-normal truncate max-w-[150px]">
                {role}
              </span>
            )}
          </div>
        </div>
      );
    }

    return (
      <button
        onClick={() => navigate(`/admin/task/edit/${task.id}`)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/60 hover:bg-amber-100 transition"
        title="Assign a team member"
      >
        <span>+ Assign</span>
      </button>
    );
  };

  const columns = [
    { 
      header: "Task Title", 
      accessor: "title", 
      sortable: true, 
      render: (t) => (
        <button 
          onClick={() => navigate(`/admin/task/view/${t.id}`)}
          className="text-left font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition"
        >
          {t.title}
        </button>
      )
    },
    { header: "Project", accessor: "projectId", sortable: true, render: (t) => getProjectName(t) },
    { header: "Assigned To", accessor: "assignedEmployeeId", sortable: true, render: (t) => getAssigneeBadge(t) },
    { header: "Due Date", accessor: "dueDate", sortable: true, render: (t) => <span className="text-[11px] font-medium text-gray-600 dark:text-slate-300">{formatDate(t.dueDate || t.due_date || t.endDate || t.end_date)}</span> },
    {
      header: "Status",
      accessor: "status",
      align: "center",
      sortable: true,
      render: (t) => (
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${getStatusColor(t.status)}`}>
          {t.status}
        </span>
      )
    },
    {
      header: "Actions",
      align: "right",
      render: (t) => (
        <TableActionButtons
          onView={checkAccess('task.view') ? () => navigate(`/admin/task/view/${t.id}`) : undefined}
          onEdit={checkAccess('task.edit') ? () => navigate(`/admin/task/edit/${t.id}`) : undefined}
          onDelete={checkAccess('task.delete') ? () => handleDelete(t.id) : undefined}
        />
      )
    }
  ];

  return (
    <div className="relative">
      <DataTable data={tasks} columns={columns} tableId="tasks" emptyMessage="No tasks found." />
    </div>
  );
};

export default TaskListView;